import { z } from 'zod';
import { defaultAttributeSelection, isKnownAttribute } from './attributes/catalog';
import { RecipeSchema } from './recipes';
import { DISCOVERY_SOURCE_IDS, type DiscoverySourceId } from './sources';

/**
 * ResearchConfiguration — the complete, versioned description of a research run.
 * Projects, presets and reruns all persist this document, so every run is reproducible.
 */
const httpUrl = () => z.url({ protocol: /^https?$/ }).max(2000);

export const CUSTOM_DATA_TYPES = ['text', 'number', 'boolean', 'url', 'email', 'phone', 'date', 'list'] as const;
export type CustomDataType = (typeof CUSTOM_DATA_TYPES)[number];

export const CustomAttributeSchema = z.object({
  key: z.string().regex(/^custom\.[a-z0-9_]{1,48}$/, 'Custom attribute keys look like "custom.currently_hiring".'),
  name: z.string().trim().min(1).max(80),
  instruction: z.string().trim().min(3).max(1000),
  dataType: z.enum(CUSTOM_DATA_TYPES),
  /** Allow AI when deterministic extraction cannot answer. */
  aiAssisted: z.boolean().default(true),
});
export type CustomAttribute = z.infer<typeof CustomAttributeSchema>;

export const LocationSchema = z.object({
  country: z.string().regex(/^[A-Z]{2}$/, 'Use a 2-letter ISO country code.'),
  city: z.string().trim().max(100).optional(),
  area: z.string().trim().max(100).optional(),
});
export type ResearchLocation = z.infer<typeof LocationSchema>;

export const CONTACT_REQUIREMENTS = ['phone_or_email', 'phone', 'email', 'phone_and_email', 'none'] as const;
export type ContactRequirement = (typeof CONTACT_REQUIREMENTS)[number];

export const CONTACT_REQUIREMENT_LABELS: Record<ContactRequirement, string> = {
  phone_or_email: 'Require phone OR email',
  phone: 'Require phone',
  email: 'Require email',
  phone_and_email: 'Require phone AND email',
  none: 'Keep all',
};

export const PRICE_LEVELS = [
  'PRICE_LEVEL_INEXPENSIVE',
  'PRICE_LEVEL_MODERATE',
  'PRICE_LEVEL_EXPENSIVE',
  'PRICE_LEVEL_VERY_EXPENSIVE',
] as const;

export const GoogleMapsOptionsSchema = z.object({
  /** Places API type filter, e.g. "restaurant", "cafe", "marketing_agency". */
  includedType: z
    .string()
    .trim()
    .regex(/^[a-z_]{2,64}$/)
    .optional(),
  minRating: z.number().min(0).max(5).optional(),
  openNowOnly: z.boolean().default(false),
  priceLevels: z.array(z.enum(PRICE_LEVELS)).max(4).default([]),
  /** Split the area into tiles to go beyond Google's 60-results-per-query limit. */
  areaTiling: z.boolean().default(true),
  languageCode: z.string().max(10).optional(),
});

/**
 * Spreadsheet import: a file you supply plus a column mapping.
 *
 * Only LinkedIn uses this now — the standalone CSV import source was removed as it duplicated
 * the same operation without adding anything. The shape stays generic so another import-backed
 * source can reuse it.
 */
export const ImportOptionsSchema = z.object({
  uploadId: z.string().min(1).max(80),
  fileName: z.string().max(260),
  /** Column header -> attribute key ("" = ignore). `person.*` keys become People, not attributes. */
  mapping: z.record(z.string(), z.string().max(120)),
  rowCount: z.number().int().min(0),
  defaultCountry: z.string().regex(/^[A-Z]{2}$/).optional(),
});
export type ImportOptions = z.infer<typeof ImportOptionsSchema>;

/**
 * LinkedIn is import-only.
 *
 * Its robots.txt disallows every path for general crawlers and prohibits automated access, so
 * this build ships no LinkedIn crawler and asks for no session cookie. The "source" is an export
 * you download from LinkedIn yourself (Sales Navigator, Connections, Recruiter), which then runs
 * through the same cleaning, merging and enrichment as every other source.
 */
export const LinkedInOptionsSchema = ImportOptionsSchema;

export const DirectoryOptionsSchema = z.object({
  /** Directory recipe ids; empty = pick the directories that cover the selected countries. */
  directories: z.array(z.string().trim().min(1).max(80)).max(10).default([]),
});

export const AnyWebsiteOptionsSchema = z.object({
  startUrls: z.array(httpUrl()).min(1).max(20),
  instruction: z.string().trim().max(1000).optional(),
  /** Inline recipe produced by the analyzer/test flow. */
  recipe: RecipeSchema.optional(),
  /** Saved recipe reference (alternative to inline recipe). */
  recipeId: z.string().max(80).optional(),
  maxPages: z.number().int().min(1).max(200).default(10),
});

export const WebsiteEnrichmentSchema = z.object({
  enabled: z.boolean().default(true),
  maxPagesPerSite: z.number().int().min(1).max(15).default(6),
  /** Render JavaScript-heavy sites with Browser Run (slower, costs browser time). */
  renderJavaScript: z.boolean().default(false),
});

export const PeopleDiscoverySchema = z.object({
  /**
   * Switch for a *LinkedIn* people search, which this build does not contain — there is no
   * handler for the task kind, so `planPhase` will not create the work (see
   * `jobs/handlers/index.ts`). It stays in the schema, and off, so a stored configuration that
   * set it still parses. People are collected from company websites regardless of this flag,
   * which is why the wizard offers no toggle for it.
   */
  enabled: z.boolean().default(false),
  /** Role families to look for, e.g. ["HR", "Marketing"]. */
  roles: z.array(z.string().trim().min(1).max(60)).max(10).default([]),
  /** Cap on people kept per company, applied to website-sourced people. */
  maxPerCompany: z.number().int().min(1).max(25).default(5),
});

export const ResearchConfigSchema = z
  .object({
    version: z.literal(1).default(1),
    name: z.string().trim().min(1, 'Give the research a name.').max(120),
    description: z.string().trim().max(2000).optional(),
    sources: z.array(z.enum(DISCOVERY_SOURCE_IDS)).min(1, 'Select at least one source.').max(5),
    search: z
      .object({
        keywords: z.array(z.string().trim().min(1).max(120)).max(20).default([]),
        locations: z.array(LocationSchema).max(50).default([]),
        targetPerSource: z.number().int().min(1).max(10_000).default(250),
        language: z.enum(['auto', 'en', 'ar', 'fr']).default('auto'),
      })
      .prefault({}),
    attributes: z.object({
      selected: z.array(z.string().min(1).max(120)).min(1, 'Select at least one attribute.').max(300),
      custom: z.array(CustomAttributeSchema).max(25).default([]),
    }),
    enrichment: z
      .object({
        website: WebsiteEnrichmentSchema.prefault({}),
        /** Visit LinkedIn company pages for accounts whose LinkedIn URL is known. */
        linkedinLookup: z.boolean().default(true),
        people: PeopleDiscoverySchema.prefault({}),
      })
      .prefault({}),
    rules: z
      .object({
        contactRequirement: z.enum(CONTACT_REQUIREMENTS).default('phone_or_email'),
        autoMergeThreshold: z.number().min(0.8).max(1).default(0.9),
        reviewThreshold: z.number().min(0.5).max(0.95).default(0.7),
        excludePermanentlyClosed: z.boolean().default(true),
      })
      .prefault({}),
    sourceOptions: z
      .object({
        google_maps: GoogleMapsOptionsSchema.optional(),
        linkedin: LinkedInOptionsSchema.optional(),
        yellow_pages: DirectoryOptionsSchema.optional(),
        any_website: AnyWebsiteOptionsSchema.optional(),
      })
      .prefault({}),
  })
  .superRefine((config, ctx) => {
    const needsKeywords = config.sources.some((source) => source === 'google_maps' || source === 'yellow_pages');
    if (needsKeywords && config.search.keywords.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['search', 'keywords'], message: 'Add at least one keyword or business type.' });
    }
    // LinkedIn is import-only, so it needs a file rather than keywords.
    if (config.sources.includes('linkedin') && !config.sourceOptions.linkedin) {
      ctx.addIssue({ code: 'custom', path: ['sourceOptions', 'linkedin'], message: 'Upload your LinkedIn export to import it.' });
    }
    if ((config.sources.includes('google_maps') || config.sources.includes('yellow_pages')) && config.search.locations.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['search', 'locations'], message: 'Add at least one location.' });
    }
    if (config.sources.includes('any_website') && !config.sourceOptions.any_website) {
      ctx.addIssue({ code: 'custom', path: ['sourceOptions', 'any_website'], message: 'Add the website URL to extract from.' });
    }
    const unknown = config.attributes.selected.filter((key) => !isKnownAttribute(key));
    if (unknown.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['attributes', 'selected'], message: `Unknown attributes: ${unknown.join(', ')}` });
    }
    const keys = config.attributes.custom.map((attribute) => attribute.key);
    if (new Set(keys).size !== keys.length) {
      ctx.addIssue({ code: 'custom', path: ['attributes', 'custom'], message: 'Custom attribute names must be unique.' });
    }
    if (config.rules.reviewThreshold >= config.rules.autoMergeThreshold) {
      ctx.addIssue({ code: 'custom', path: ['rules', 'reviewThreshold'], message: 'Review threshold must be below the auto-merge threshold.' });
    }
  });

export type ResearchConfig = z.infer<typeof ResearchConfigSchema>;
export type ResearchConfigInput = z.input<typeof ResearchConfigSchema>;

export function parseResearchConfig(input: unknown): ResearchConfig {
  return ResearchConfigSchema.parse(input);
}

export interface DefaultConfigOptions {
  name?: string;
  sources?: DiscoverySourceId[];
}

/** A valid-shaped starting point for the research wizard with smart defaults. */
export function createDefaultConfig(options: DefaultConfigOptions = {}): ResearchConfigInput {
  const sources = options.sources ?? ['google_maps'];
  return {
    version: 1,
    name: options.name ?? 'Untitled research',
    sources,
    search: { keywords: [], locations: [], targetPerSource: 250, language: 'auto' },
    attributes: { selected: defaultAttributeSelection({ sources, websiteEnrichment: true }), custom: [] },
    enrichment: {
      website: { enabled: true, maxPagesPerSite: 6, renderJavaScript: false },
      linkedinLookup: true,
      people: { enabled: false, roles: [], maxPerCompany: 5 },
    },
    rules: { contactRequirement: 'phone_or_email', autoMergeThreshold: 0.9, reviewThreshold: 0.7, excludePermanentlyClosed: true },
    sourceOptions: {},
  };
}

/** Human-readable location label ("Smouha, Alexandria, EG"). */
export function locationLabel(location: ResearchLocation, countryName?: (code: string) => string): string {
  return [location.area, location.city, countryName ? countryName(location.country) : location.country]
    .filter(Boolean)
    .join(', ');
}
